import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { logger } from "../utils/logger";

// Configuração base da API
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:2222/api";

// Instância do axios com configurações padrão
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // ESSENCIAL para cookies HttpOnly
});

// Flag para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

// Função para processar a fila de requisições após o refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Função para fazer logout e limpar estado
const performLogout = () => {
  localStorage.removeItem("hasValidToken");

  // Redirecionar para login apenas se não estivermos já na página de login
  if (
    window.location.pathname !== "/" &&
    window.location.pathname !== "/login"
  ) {
    window.location.href = "/";
  }
};

// Interceptor de requisição - não precisa adicionar token pois usa cookies HttpOnly
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Os cookies HttpOnly são enviados automaticamente pelo navegador
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de resposta para lidar com refresh token
api.interceptors.response.use(
  (response) => {
    // Com cookies HttpOnly, não precisamos processar headers de token
    // O backend já define os cookies automaticamente
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Se o erro for 401 e não for uma tentativa de retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Evitar retry em endpoints de auth para evitar loops infinitos
      if (
        originalRequest.url?.includes("/auth/login") ||
        originalRequest.url?.includes("/auth/refresh") ||
        originalRequest.url?.includes("/auth/logout")
      ) {
        return Promise.reject(error);
      }

      // Se já estamos fazendo refresh, adicionar à fila
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        logger.info("🔄 Tentando refresh token...");

        // Tentar fazer refresh - o backend vai definir novos cookies automaticamente
        await api.post("/auth/refresh");

        logger.info("✅ Refresh token bem-sucedido");

        // Marcar como autenticado
        localStorage.setItem("hasValidToken", "true");

        // Processar fila de requisições pendentes
        processQueue(null, "refreshed");

        // Tentar a requisição original novamente
        return api(originalRequest);
      } catch (refreshError) {
        logger.error("❌ Falha no refresh token:", refreshError);

        // Processar fila com erro
        processQueue(refreshError, null);

        // Fazer logout e redirecionar
        performLogout();

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Para outros tipos de erro, rejeitar normalmente
    return Promise.reject(error);
  }
);

// Função utilitária para verificar se o token está válido
export const checkTokenValidity = async (): Promise<boolean> => {
  try {
    await api.get("/auth/me");
    return true;
  } catch (error) {
    return false;
  }
};

// Função para inicializar a verificação de token na inicialização da app
export const initializeAuth = async (): Promise<boolean> => {
  const hasToken = localStorage.getItem("hasValidToken");

  if (!hasToken) {
    return false;
  }

  try {
    // Verificar se o token ainda é válido
    const isValid = await checkTokenValidity();

    if (!isValid) {
      // Token inválido, limpar estado
      performLogout();
      return false;
    }

    return true;
  } catch (error) {
    logger.error("Erro ao verificar token:", error);
    performLogout();
    return false;
  }
};

export default api;
